import React, { useEffect, useRef } from "react";

// SASS
import "./Part2.scss";

// COMPONENTS
import Step from "../Step/Step";

// HELPERS
import animate from "../../utils/animate";

// LIBRARIES
import scrollama from "scrollama";

// ASSETS
import handImg from "../../assets/img/Pointer-Finger.png";
import watchImg from "../../assets/img/wrist-watch.svg";
import germ1Img from "../../assets/img/germ-1-white.svg";
import germ2Img from "../../assets/img/germ-2-white.svg";
import germ3Img from "../../assets/img/germ-3-white.svg";
import germ4Img from "../../assets/img/germ-4-white.svg";
import bracketImg from "../../assets/img/bracket.png";

// FUNCTION EXPORT
export default function Part2({ step }) {
  // SCROLLAMA INSTANCE
  const scrolly = scrollama();

  // ANIMATION STEP DATA
  const animationSequence = [
    {
      stepNumber: "0",
      height: "600px",
      description: "change bg color",
      offset: 0,
    },
    { stepNumber: "1", height: null, description: "fade in container" },
    { stepNumber: "2", height: null, description: "spacer" },
    {
      stepNumber: "3",
      height: null,
      description: "hide germs, change remember box",
    },
    { stepNumber: "4", height: "750px", description: "fade in text2" },
    { stepNumber: "5", height: "750px", description: "fade in bracket" },
    {
      stepNumber: "6",
      height: "1000px",
      description: "fade in text3, text4",
    },
    { stepNumber: "7", height: null, description: "grow sleeve" },
    {
      stepNumber: "8",
      height: "500px",
      description: "change background color to white",
    },
    {
      stepNumber: "9",
      height: "500px",
      description: "spacer",
    },
  ];

  // REFS
  const p2ContainerEl = useRef(null);
  const rememberEl = useRef(null);
  const germContainerEl = useRef(null);
  const text2El = useRef(null);
  const text3El = useRef(null);
  const text4El = useRef(null);
  const bracketEl = useRef(null);
  const sleeveEl = useRef(null);

  // RUN WHEN THE COMPONENT MOUNTS
  useEffect(() => {
    scrolly
      .setup({
        step: `.${step}`,
        debug: false,
        threshold: 1,
        progress: true,
      })
      .onStepEnter(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "0": {
            animate.changeBodyBgColorDown("#1B5E80", direction);
            break;
          }
          case "1": {
            animate.fadeInOnEnter(p2ContainerEl.current, direction);
            break;
          }
          case "3": {
            animate.addClassOnEnter(rememberEl.current, "lighten", direction);
            animate.fadeOutOnEnter(germContainerEl.current, direction);
            break;
          }
          case "4": {
            animate.fadeInOnEnter(text2El.current, direction);
            break;
          }
          case "5": {
            animate.fadeInOnEnter(bracketEl.current, direction);
            break;
          }
          case "6": {
            animate.fadeInOnEnter(text3El.current, direction);
            animate.fadeInOnEnter(text4El.current, direction);
            break;
          }
          case "7": {
            animate.addClassOnEnter(sleeveEl.current, "grow", direction);
            break;
          }
          case "8": {
            animate.changeBodyBgColorDown("#FFFFFF", direction);
            break;
          }
          default:
            break;
        }
      })
      .onStepProgress(({ element, progress }) => {
        const step = element.dataset.step;
        switch (step) {
          case "7": {
            growSleeve(progress);
            break;
          }
          default:
            break;
        }
      })
      .onStepExit(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "0": {
            animate.changeBodyBgColorUp("#FFFFFF", direction);
            break;
          }
          case "1": {
            animate.fadeOutOnExit(p2ContainerEl.current, direction);
            break;
          }
          case "3": {
            animate.removeClassOnExit(rememberEl.current, "lighten", direction);
            animate.fadeInOnExit(germContainerEl.current, direction);
            break;
          }
          case "4": {
            animate.fadeOutOnExit(text2El.current, direction);
            break;
          }
          case "5": {
            animate.fadeOutOnExit(bracketEl.current, direction);
            break;
          }
          case "6": {
            animate.fadeOutOnExit(text3El.current, direction);
            animate.fadeOutOnExit(text4El.current, direction);
            break;
          }
          case "7": {
            animate.removeClassOnExit(sleeveEl.current, "grow", direction);
            break;
          }
          case "8": {
            animate.changeBodyBgColorUp("#1B5E80", direction);
            break;
          }
          default:
            break;
        }
      });
  }, [scrolly, step]);

  // CUSTOM FUNCTIONS
  const growSleeve = (progress) => {
    const currentWidth = window.innerWidth;
    const currentWidthRems = currentWidth / 16;
    const targetWidth = currentWidthRems - 7.8 + 15;

    const translateX = -6 + 6 * progress;
    const translateY = 36 - 36 * progress;
    const scaleY = 1 + 0.8 * progress;
    const width = 7.8 + targetWidth * progress;

    // if (progress > 0.1) {
    //   sleeveEl.current.style.zIndex = 10;
    // } else {
    //   sleeveEl.current.style.zIndex = 1;
    // }
    sleeveEl.current.style.transform = `translate(${translateX}rem,${translateY}rem) scale(1,${scaleY})`;
    sleeveEl.current.style.width = `${width}rem`;
  };

  return (
    <section className="wrapper">
      <div className="sticky-container">
        {/* STICKY */}
        <div className="part2">
          <div className="part2-container" ref={p2ContainerEl}>
            <div className="remember" ref={rememberEl}>
              Remember
            </div>
            <div className="hand">
              <img src={handImg} alt="" className="img-fluid" />
            </div>
            <div className="watch">
              <img src={watchImg} alt="" className="img-fluid" />
            </div>
            <div className="sleeve" ref={sleeveEl}></div>
            <div className="germ-container" ref={germContainerEl}>
              <div className="germ germ-1 rotate delay-random-1">
                <img src={germ1Img} alt="Germ" className="img-fluid" />
              </div>
              <div className="germ germ-2 rotate delay-random-2">
                <img src={germ2Img} alt="Germ" className="img-fluid" />
              </div>

              <div className="germ germ-3 rotate delay-random-3">
                <img src={germ3Img} alt="Germ" className="img-fluid" />
              </div>
              <div className="germ germ-4 rotate delay-random-4">
                <img src={germ4Img} alt="Germ" className="img-fluid" />
              </div>
            </div>
            <div className="text-container">
              <div className="text text-1">
                The job of a disinfectant is to kill germs.
              </div>
              <div className="text text-2" ref={text2El}>
                CONTACT TIME
              </div>
              <div className="bracket" ref={bracketEl}>
                <img src={bracketImg} alt="" className="img-fluid" />
              </div>
              <div className="text text-3" ref={text3El}>
                is important to determine how long a disinfectant should be on a
                surface before it is wiped away or disturbed.{" "}
              </div>
              <div className="text text-4" ref={text4El}>
                This is to make sure germs die.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STEPS */}
      {animationSequence.map(({ stepNumber, height, description, offset }) => {
        return (
          <Step
            stepSection={step}
            stepNumber={stepNumber}
            stepDescription={description}
            height={height}
            key={stepNumber}
            offset={offset}
          />
        );
      })}
    </section>
  );
}
